/**
 * SchedulerTab - Campaign scheduler settings and SMS/Email blast controls (FULLY DYNAMIC)
 */
import { Calendar, Send, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCampaignTheme, LEGACY_CAMPAIGN_NAMES } from './campaignUtils';

export default function SchedulerTab({
  report,
  allCampaigns,
  schedules,
  integrationStatus,
  savingSchedule,
  sendingBlast,
  onUpdateSchedule,
  onSaveSchedule,
  onSendBlast
}) {
  // Build dynamic campaign list from allCampaigns OR report.campaigns
  const campaignList = allCampaigns?.length > 0 
    ? allCampaigns 
    : report?.campaigns?.map(c => ({ campaign_id: c.campaign_id, title: c.title, theme: c.theme })) || [];

  return (
    <div className="space-y-8">
      {/* Integration Status Banner */}
      <div className="bg-card border rounded-2xl p-4">
        <p className="font-medium mb-3">Integration Status</p>
        <div className="flex flex-wrap gap-4">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            integrationStatus?.mailchimp?.status === 'connected' 
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            <Mail className="w-4 h-4" />
            <span>Mailchimp: {integrationStatus?.mailchimp?.status === 'connected' 
              ? `Connected (${integrationStatus.mailchimp.list_name})` 
              : 'Not configured'}</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            integrationStatus?.twilio?.configured 
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            <Phone className="w-4 h-4" />
            <span>Twilio SMS: {integrationStatus?.twilio?.configured ? 'Connected' : 'Not configured'}</span>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 text-amber-800 dark:text-amber-200">
        <p className="font-medium">Campaign Scheduler</p>
        <p className="text-sm mt-1">Set start/end dates and schedule SMS & Email blasts. {!integrationStatus?.twilio?.configured && !integrationStatus?.mailchimp?.configured && 'Add integration keys in backend .env to enable automated sending.'}</p>
      </div>

      {/* DYNAMIC CAMPAIGN LIST */}
      {campaignList.map(campaignData => {
        const id = campaignData.campaign_id;
        const theme = getCampaignTheme(id, campaignData.theme);
        const schedule = schedules.find(s => s.campaign_id === id) || { campaign_id: id };
        const reportCampaign = report?.campaigns?.find(c => c.campaign_id === id);
        const campaignName = campaignData.title || LEGACY_CAMPAIGN_NAMES[id] || `Campaign ${id}`;
        
        return (
          <div key={id} className={`bg-card rounded-2xl border-2 ${theme.border} overflow-hidden`} data-testid={`scheduler-campaign-${id}`}>
            {/* Header */}
            <div className={`bg-gradient-to-r ${theme.gradient} p-4 text-white flex items-center justify-between`}>
              <div>
                <p className="font-medium">Campaign {id}</p>
                <p className="text-sm opacity-90 truncate max-w-xs">{campaignName}</p>
                <p className="text-xs opacity-75">{reportCampaign?.signups || 0} signups</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm">Active</span>
                <input
                  type="checkbox"
                  checked={schedule.is_active !== false}
                  onChange={(e) => onUpdateSchedule(id, { is_active: e.target.checked })}
                  className="rounded"
                />
              </label>
            </div>

            <div className="p-6 grid md:grid-cols-2 gap-6">
              {/* Schedule Dates */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Campaign Schedule
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Start Date</Label>
                    <Input
                      type="date"
                      value={schedule.start_date || ''}
                      onChange={(e) => onUpdateSchedule(id, { start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">End Date</Label>
                    <Input
                      type="date"
                      value={schedule.end_date || ''}
                      onChange={(e) => onUpdateSchedule(id, { end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Scheduled Blast Date</Label>
                  <Input
                    type="date"
                    value={schedule.scheduled_blast_date || ''}
                    onChange={(e) => onUpdateSchedule(id, { scheduled_blast_date: e.target.value })}
                    placeholder="When to send blast to all signups"
                  />
                  <p className="text-xs text-muted-foreground">Date when SMS/Email will be sent to all signups</p>
                </div>
              </div>

              {/* Automation Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Automation
                </h4>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-muted rounded-xl">
                    <input
                      type="checkbox"
                      checked={schedule.send_sms_on_signup || false}
                      onChange={(e) => onUpdateSchedule(id, { send_sms_on_signup: e.target.checked })}
                      className="rounded"
                    />
                    <div>
                      <p className="font-medium text-sm text-foreground">Send SMS on Signup</p>
                      <p className="text-xs text-muted-foreground">Immediately send welcome SMS (requires Twilio)</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-muted rounded-xl">
                    <input
                      type="checkbox"
                      checked={schedule.send_email_on_signup || false}
                      onChange={(e) => onUpdateSchedule(id, { send_email_on_signup: e.target.checked })}
                      className="rounded"
                    />
                    <div>
                      <p className="font-medium text-sm text-foreground">Send Email on Signup</p>
                      <p className="text-xs text-muted-foreground">Immediately send welcome email (requires Mailchimp)</p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => onSendBlast(id, 'sms')}
                    disabled={sendingBlast?.campaignId === id}
                  >
                    {sendingBlast?.campaignId === id && sendingBlast?.type === 'sms' 
                      ? 'Sending...' 
                      : `Send SMS Blast (${reportCampaign?.signups || 0})`
                    }
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => onSendBlast(id, 'email')}
                    disabled={sendingBlast?.campaignId === id}
                  >
                    {sendingBlast?.campaignId === id && sendingBlast?.type === 'email' 
                      ? 'Sending...' 
                      : `Send Email Blast (${reportCampaign?.signups || 0})`
                    }
                  </Button>
                </div>
              </div>
            </div>

            {/* Custom Messages */}
            <div className="px-6 pb-6">
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  Custom Messages (optional)
                </summary>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Custom SMS Message</Label>
                    <Input
                      value={schedule.custom_sms_message || ''}
                      onChange={(e) => onUpdateSchedule(id, { custom_sms_message: e.target.value })}
                      placeholder="Your coupon code {coupon_code} is ready! Valid June 1-30."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Custom Email Subject</Label>
                    <Input
                      value={schedule.custom_email_subject || ''}
                      onChange={(e) => onUpdateSchedule(id, { custom_email_subject: e.target.value })}
                      placeholder="Your Bulaccino Founding Member Coupon Is Ready!"
                    />
                  </div>
                </div>
              </details>
            </div>

            {/* Save Button */}
            <div className="px-6 pb-6">
              <Button
                onClick={() => onSaveSchedule(id)}
                disabled={savingSchedule === id}
                className={`w-full rounded-full bg-gradient-to-r ${theme.gradient} text-white`}
              >
                {savingSchedule === id ? 'Saving...' : 'Save Schedule'}
              </Button>
            </div>
          </div>
        );
      })}
      
      {/* Empty state */}
      {campaignList.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No campaigns found. Create a campaign to schedule blasts.</p>
        </div>
      )}
    </div>
  );
}
